import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import backgroundImage from './assets/pngtree-scene-with-geometrical-forms-the-poster-model-minimal-background-render-image_452981.jpg';
import logoImage from './assets/GX Transparent logo.png';
import { toast, ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link, useNavigate } from 'react-router-dom';

const SignUp = () => {
  const [submitted, setSubmitted] = useState(false);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const navigate = useNavigate(); 

  const schema = yup.object().shape({
    userName: yup
      .string()
      .required('Full Name is required')
      .matches(/^[a-zA-Z ]+$/, 'Only letters are allowed for Name')
      .min(2, 'Name must be at least 2 characters')
      .max(20, 'Name must be at most 20 characters'),
    email: yup
      .string()
      .required('Email is required')
      .email('Email is invalid')
      .matches(/^[a-zA-Z0-9._%+-]+@gxinetworks\.com$/, 'Email only valid for gxinetworks.com domain'),
    dob: yup.string().required('DOB is required'),
    password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters'),
    team: yup.string().required('Team is required'),
    role: yup.string().required('Role is required'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const selectedTeamId = watch('team'); // Watches the selected team ID

  const fetchTeams = async () => {
    try {
      const response = await fetch('http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/teams');
      if (!response.ok) throw new Error('Failed to fetch teams');
      const data = await response.json();
      setTeams(data || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchRoles = async (teamId) => {
    if (!teamId) return;
    try {
      const response = await fetch(`http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/roles/${teamId}`);
      if (!response.ok) throw new Error('Failed to fetch roles');
      const data = await response.json();
      setRoles(data || []);
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (selectedTeamId) {
      fetchRoles(selectedTeamId);
    } else {
      setRoles([]); // Reset roles if no team is selected
    }
  }, [selectedTeamId]);

  const onSubmit = async (data) => {
    const formattedData = {
      userName: data.userName,
      email: data.email,
      dob: data.dob,
      password: data.password,
      teamId: data.team,  // Correct field name
      roleId: data.role   // Correct field name
    };
  
    try {
      const response = await fetch('http://hr-onboarding-website-backend-LB-1197701009.us-east-1.elb.amazonaws.com:5000/SignUpWithHR', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedData),
      });
  
      const responseData = await response.json();
  
      if (response.ok) {
        toast.success('User Created Successfully', { position: 'top-center', transition: Slide });
        reset();
        setSubmitted(true);
      } else {
        // console.log(responseData, 'ress');
        toast.error(responseData.error);
      }
    } catch (error) {
      toast.error('Failed to submit form. Please try again later.');
    }
  };
  

  // useEffect(() => {
  //   if (submitted) {
  //     const timer = setTimeout(() => navigate('/'), 3000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [submitted, navigate]);

  return (
    <>
      <ToastContainer />
      <div className="min-h-screen flex items-center justify-center bg-cover bg-center relative" style={{ backgroundImage: `url(${backgroundImage})` }}>
        <img src={logoImage} alt="Logo" className="absolute top-0 left-4 w-52 h-32" />

        <div className="bg-white bg-opacity-10 p-8 rounded-xl shadow-lg max-w-md w-full mt-24 mb-10">
          <h2 className="text-3xl font-bold text-center text-primary-dark">HR Portal</h2>
          <h2 className="text-2xl font-bold mb-6 text-center text-primary-dark">Create an Account</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-2">
              <label className="block text-neutral-dark mb-2 font-medium">UserName</label>
              <input type="text" {...register('userName')} className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
              {errors.userName && <p className="text-red-500 text-sm mt-1">{errors.userName.message}</p>}
            </div>

            <div className="mb-2">
              <label className="block text-neutral-dark mb-2 font-medium">Email</label>
              <input type="email" {...register('email')} className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
            </div>

            <div className="mb-2">
              <label className="block text-neutral-dark mb-2 font-medium">DOB</label>
              <input type="date" {...register('dob')} className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
              {errors.dob && <p className="text-red-500 text-sm mt-1">{errors.dob.message}</p>}
            </div>

            <div className="mb-2">
              <label className="block text-neutral-dark mb-2 font-medium">Password</label>
              <input
                type="password"
                {...register('password')}
                className="w-full px-2 py-2 border border-neutral-light rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>}
            </div>

            <div className="mb-2">
              <label className="block text-neutral-dark mb-2 font-medium">Team</label>
              <select {...register('team')} className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary">
                <option value="">Select a team</option>
                {teams.map((team) => (
                  <option key={team._id} value={team._id}>{team.teams}</option>
                ))}
              </select>
              {errors.team && <p className="text-red-500 text-sm mt-1">{errors.team.message}</p>}
            </div>

            <div className="mb-2">
              <label className="block text-neutral-dark mb-2 font-medium">Role</label>
              <select {...register('role')} className="w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-primary">
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>{role.roleName}</option>
                ))}
              </select>
              {errors.role && <p className="text-red-500 text-sm mt-1">{errors.role.message}</p>}
            </div>

            <button type="submit" className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark transition duration-300">Sign Up</button>
          </form>

          <p className="mt-6 text-center text-neutral-dark">
            Already have an account? <Link to="/SignInWithHR" className="text-primary hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </>
  );
};

export default SignUp;
